import * as React from 'react';
import * as wijmo from '@grapecity/wijmo';
import { CollectionView } from '@grapecity/wijmo';

import * as wjChart from '@grapecity/wijmo.react.chart';
import * as wjGrid from '@grapecity/wijmo.grid';
import { FlexGrid, FlexGridColumn } from '@grapecity/wijmo.react.grid';

import { Col, Row, Container } from 'react-bootstrap';
import { useEffect, useState, useRef } from 'react';
import { toInt, toFloat, calcRate } from 'utils/numberUtils';
import './Analysis.css';
import { AnalysisKind, ComparisonKind } from 'views/Analysis/analysisUtils';
import { isBeforeThisMonth } from 'utils/dateUtils';
import AnalysisGlaphBigSmall from 'views/Analysis/AnalysisGlaphBigSmall';
import AnalysisGlaphCurrentBefore from 'views/Analysis/AnalysisGlaphCurrentBefore';

// カテゴリ毎の分析情報
function AnalysisCategory(props) {
  // 分析データリスト
  const analysisList = props.analysisList;
  // 選択中の期
  const selectedPeriod = props.selectedPeriod;
  // 集計項目
  const summaryItems = props.summaryItems;
  // 分析種別
  const analysisKind = props.analysisKind;
  // 比較種別
  const comparisonKind = props.comparisonKind;
  // ヘッダー
  const categoryHeader = props.categoryHeader;
  // プレフィックス
  const prefix = analysisKind.prefix;
  // カテゴリ名
  const categoryName = props.categoryName;
  // グラフ非表示フラグ
  const glaphHidden = props.glaphHidden ?? false;

  // 比較用列ヘッダ名
  const headerName1 =
    comparisonKind.key === ComparisonKind.CURRENT_BEFORE ? `${selectedPeriod - 1}期` : '工事';
  const headerName2 =
    comparisonKind.key === ComparisonKind.CURRENT_BEFORE ? `${selectedPeriod}期` : '管・小';

  // 比較用のキー名
  const key1 = comparisonKind.key1;
  const key2 = comparisonKind.key2;

  // キーのCss
  const key1Css = props.key1Css ?? comparisonKind.key1Css;
  const key2Css = props.key2Css ?? comparisonKind.key2Css;

  // 合計用のキー名
  const keyTotal = props.keyTotal ?? 'TOTAL';

  // グリッドのキー
  const [gridKey, setGridKey] = useState(0);

  // 総計行のマージ処理
  class CustomMergeManager extends wjGrid.MergeManager {
    getMergedRange(panel, r, c, clip = true) {
      if (panel.cellType == wjGrid.CellType.ColumnFooter) {
        const footerValue = panel.getCellData(r, 0, true);
        if (footerValue === '総計' || footerValue === '総契約率') {
          var rng = new wjGrid.CellRange(r, c);
          if (c % 2 === 1) {
            rng.col = c;
            rng.col2 = c + 1;
            return rng;
          } else if (c !== 0) {
            rng.col = c - 1;
            rng.col2 = c;
            return rng;
          }
        }
      }
      // 通常のマージ処理
      return super.getMergedRange(panel, r, c, clip);
    }
  }

  useEffect(() => {
    // グリッドの再描画のためにキーを変更
    setGridKey((prev) => prev + 1);
  }, [analysisList, analysisKind, summaryItems, comparisonKind]);

  //  グリッドの初期化
  function onInitialized(grid) {
    grid.mergeManager = new CustomMergeManager();
    // ヘッダーを2段にする
    var extraRow = new wjGrid.Row();
    extraRow.allowMerging = true;
    extraRow.cssClassAll = 'header-center';
    var panel = grid.columnHeaders;
    panel.rows.splice(0, 0, extraRow);
    let summaryItemsRow = 0; // 集計項目の行数

    // ヘッダーが指定されている場合は3段にする
    if (categoryHeader) {
      // ヘッダー行を追加
      var headRow = new wjGrid.Row();
      headRow.cssClassAll = 'header-center';
      headRow.allowMerging = true;
      panel.rows.splice(0, 0, headRow);

      // ヘッダー行を追加
      grid.columns.forEach((_, i) => {
        panel.setCellData(0, i, categoryHeader.find((x) => x.start <= i && x.end >= i)?.category);
      });

      summaryItemsRow = 1; // 集計項目の行数を２段目に設定
    }

    // 集計項目のヘッダーを設定
    let colIndex = 1;
    summaryItems.forEach((item) => {
      for (let i = 0; i < 2; i++) {
        panel.setCellData(summaryItemsRow, colIndex, item.label);
        colIndex++;
      }
    });
    // 合計のヘッダーを設定
    for (let i = 0; i < 2; i++) {
      panel.setCellData(summaryItemsRow, colIndex, '合計');
      colIndex++;
    }

    // 垂直方向にマージ
    ['MONTH'].forEach(function (binding) {
      let col = grid.getColumn(binding);
      col.allowMerging = true;
      panel.setCellData(0, col.index, col.header);
      if (categoryHeader) {
        panel.setCellData(1, col.index, col.header);
      }
    });

    grid.formatItem.addHandler((sender, e) => {
      // ヘッダーの境界値の線を太くする
      if (categoryHeader?.some((x) => x.end + 1 === e.col)) {
        e.cell.style.borderLeft = 'double';
      }

      // 工／管・小の総計の項目を太くする
      if (comparisonKind.key === ComparisonKind.BIG_SMALL && e.col === sender.columns.length - 1) {
        e.cell.style.borderLeft = 'double';
      }

      // フッターセルのスタイル設定
      if (e.panel === sender.columnFooters) {
        const colIndex = e.col;
        const rowIndex = e.row;

        // フッターの1行目の線を太くする
        if (rowIndex === 0) {
          e.cell.style.borderTop = 'double';
        }
        if (
          [0, 1, 3].includes(rowIndex) &&
          colIndex >= 1 &&
          colIndex <= summaryItems.length * 2 &&
          colIndex % 2 === 1
        ) {
          // 前期
          e.cell.style.backgroundColor = '#fce4d6';
        } else if (
          [0, 1, 3].includes(rowIndex) &&
          colIndex >= 1 &&
          colIndex <= summaryItems.length * 2 &&
          colIndex % 2 === 0
        ) {
          // 今期
          e.cell.style.backgroundColor = '#ddebf7';
        } else if ([0, 3].includes(rowIndex) && colIndex > summaryItems.length * 2) {
          // 合計
          e.cell.style.backgroundColor = colIndex % 2 == 0 ? '#ddebf7' : '#fce4d6';
        } else {
          // 他のセルの背景色をデフォルトに戻す
          e.cell.style.backgroundColor = '';
        }
      }
    });

    // フッターを条件に合わせて追加
    let footerCount = comparisonKind.key === ComparisonKind.CURRENT_BEFORE ? 3 : 2;
    if (analysisKind.key == AnalysisKind.KEIYAKU) {
      footerCount = footerCount + 1; // 契約件数の場合は契約率の行を追加
      if (comparisonKind.key === ComparisonKind.BIG_SMALL) {
        footerCount = footerCount + 1; // 工／管・小の場合は総契約率の行を追加
      }
    }
    for (let i = 0; i < footerCount; i++) {
      let footerRow = new wjGrid.Row();
      grid.columnFooters.rows.push(footerRow);
    }

    // データ変更時に合計更新
    grid.collectionView.collectionChanged.addHandler(updateFooter);

    updateFooter(grid);
  }

  // 合計値を計算してフッターに設定
  function updateFooter(grid) {
    let data = grid?.itemsSource?.items;
    if (!data || data.length === 0) return;
    let panel = grid.columnFooters;

    // 前月までの合計を返す共通関数
    const sumUntilLastMonth = (binding) =>
      data
        .filter((item) => isBeforeThisMonth(item['MONTH'], selectedPeriod))
        .reduce((sum, item) => sum + (item[binding] || 0), 0);

    // <合計>
    let row = 0;
    panel.setCellData(row, 0, '合計');
    grid.columns.forEach((col, colIndex) => {
      // 月行は無視
      if (col.binding === 'MONTH') return;
      const binding = col.binding;
      let total = data.reduce((sum, item) => sum + (item[binding] || 0), 0);
      panel.setCellData(row, colIndex, total);
    });
    row++;

    // <構成比>※昨対比の場合
    if (comparisonKind.key === ComparisonKind.CURRENT_BEFORE) {
      panel.setCellData(row, 0, '構成比');
      grid.columns.forEach((col, colIndex) => {
        // 月行は無視
        if (col.binding === 'MONTH') return;
        const targetKey = col.binding.includes(key1) ? key1 : key2;
        const binding = col.binding;
        let total = data.reduce((sum, item) => sum + (item[binding] || 0), 0);
        let allTotal = data.reduce(
          (sum, item) => sum + (item[`${prefix}${targetKey}_${keyTotal}`] || 0),
          0
        );
        let kouseiRate = calcRate(total, allTotal, 3);
        panel.setCellData(row, colIndex, wijmo.format('{val:p1}', { val: kouseiRate }));
      });
      row++;
    }

    // <前月まで>※昨対比の場合
    if (comparisonKind.key === ComparisonKind.CURRENT_BEFORE) {
      panel.setCellData(row, 0, '前月まで');
      grid.columns.forEach((col, colIndex) => {
        // 月行は無視
        if (col.binding === 'MONTH') return;

        const binding = col.binding;

        if (binding.includes(key1)) {
          // [前期] 前月までの合計
          panel.setCellData(row, colIndex, sumUntilLastMonth(binding));
        } else {
          // [今期]
          // ※伸び率は前期・今期とも「前月まで」で揃える
          let total = sumUntilLastMonth(binding);
          let totalBefore = sumUntilLastMonth(binding.replace(key2, key1));
          let upRate = calcRate(total, totalBefore, 3);
          panel.setCellData(row, colIndex, wijmo.format('{val:p1}', { val: upRate }));
        }
      });
      row++;
    }

    // <総計>※工／管・小の場合
    if (comparisonKind.key === ComparisonKind.BIG_SMALL) {
      panel.setCellData(row, 0, '総計');

      grid.columns.forEach((col, colIndex) => {
        // 月行は無視
        if (col.binding === 'MONTH') return;

        const binding = col.binding;

        // 奇数行のみ
        if (colIndex % 2 === 1 && colIndex < grid.columns.length - 1) {
          // 合計
          let total1 = data.reduce((sum, item) => sum + (item[binding] || 0), 0);
          let total2 = data.reduce((sum, item) => sum + (item[binding.replace(key1, key2)] || 0), 0);
          panel.setCellData(row, colIndex, total1 + total2);
        }
      });
      row++;
    }

    // <契約率>
    if (analysisKind.key == AnalysisKind.KEIYAKU) {
      panel.setCellData(row, 0, '契約率');

      grid.columns.forEach((col, colIndex) => {
        // 月行は無視
        if (col.binding === 'MONTH') return;

        const binding = col.binding;
        // 合計
        let total = data.reduce((sum, item) => sum + (item[binding] || 0), 0);
        // 相談件数の項目名
        const soudanBinding = binding.replace('KEI_', '');
        const soudanTotal = data.reduce((sum, item) => sum + (item[soudanBinding] || 0), 0);
        const keiRate = calcRate(total, soudanTotal, 3);
        panel.setCellData(row, colIndex, wijmo.format('{val:p1}', { val: keiRate }));
      });
      row++;
    }

    // <総契約率>
    if (
      analysisKind.key == AnalysisKind.KEIYAKU &&
      comparisonKind.key === ComparisonKind.BIG_SMALL
    ) {
      panel.setCellData(row, 0, '総契約率');

      grid.columns.forEach((col, colIndex) => {
        // 月行は無視
        if (col.binding === 'MONTH') return;

        const binding = col.binding;
        // 合計
        let total1 = data.reduce((sum, item) => sum + (item[binding] || 0), 0);
        let total2 = data.reduce((sum, item) => sum + (item[binding.replace(key1, key2)] || 0), 0);
        // 相談件数の項目名
        const soudanBinding = binding.replace('KEI_', '');
        const soudanTotal1 = data.reduce((sum, item) => sum + (item[soudanBinding] || 0), 0);
        const soudanTotal2 = data.reduce(
          (sum, item) => sum + (item[soudanBinding.replace(key1, key2)] || 0),
          0
        );
        const keiRate = calcRate(total1 + total2, soudanTotal1 + soudanTotal2, 3);
        panel.setCellData(row, colIndex, wijmo.format('{val:p1}', { val: keiRate }));
      });
      row++;
    }
  }

  return (
    <Container fluid className="p-0">
      <Row className="mt-2">
        <Col className="pl-3" md="12">
          <FlexGrid
            key={gridKey}
            isReadOnly={true}
            allowMerging="ColumnHeaders"
            allowSorting={false}
            allowDragging={false}
            itemsSource={analysisList}
            headersVisibility={'Column'}
            alternatingRowStep={0}
            initialized={onInitialized}
            frozenColumns={1}
          >
            <FlexGridColumn
              binding="MONTH"
              header="月"
              allowMerging={true}
              width={60}
              align="center"
            ></FlexGridColumn>
            {summaryItems
              .flatMap((x) => [
                {
                  key: prefix + key1 + '_' + x.key,
                  header: `${headerName1}`,
                  cssClass: `${key1Css}`,
                },
                {
                  key: prefix + key2 + '_' + x.key,
                  header: `${headerName2}`,
                  cssClass: `${key2Css}`,
                },
              ])
              .map((item) => (
                <FlexGridColumn
                  key={item.key}
                  binding={item.key}
                  header={item.header}
                  cssClass={item.cssClass}
                  format="n0"
                  width={analysisKind.cellWidth}
                  align={analysisKind.cellAlign}
                />
              ))}
            {/* 合計列 */}
            <FlexGridColumn
              binding={`${prefix}${key1}_${keyTotal}`}
              cssClass="total_cell"
              header={`${headerName1}`}
              format="n0"
              width={analysisKind.cellWidth}
              align={analysisKind.cellAlign}
            ></FlexGridColumn>
            <FlexGridColumn
              binding={`${prefix}${key2}_${keyTotal}`}
              cssClass="total_cell"
              header={`${headerName2}`}
              format="n0"
              width={analysisKind.cellWidth}
              align={analysisKind.cellAlign}
            ></FlexGridColumn>

            {/* 工／管・小の場合は総計を表示（今期の合計を表示） */}
            {comparisonKind.key === ComparisonKind.BIG_SMALL && (
              <FlexGridColumn
                binding={`${prefix}CURRENT_${keyTotal}`}
                cssClass="total_cell"
                header={`総合計`}
                format="n0"
                width={analysisKind.cellWidth}
                align={analysisKind.cellAlign}
              ></FlexGridColumn>
            )}
          </FlexGrid>
        </Col>
      </Row>
      {/* 昨対比のグラフ */}
      {!glaphHidden && comparisonKind.key === ComparisonKind.CURRENT_BEFORE && (
        <AnalysisGlaphCurrentBefore
          analysisList={analysisList}
          summaryItems={summaryItems}
          analysisKind={analysisKind}
          comparisonKind={comparisonKind}
          categoryName={categoryName}
          selectedPeriod={selectedPeriod}
          keyTotal={keyTotal}
        />
      )}
      {/* 工／管・小のグラフ */}
      {!glaphHidden && comparisonKind.key === ComparisonKind.BIG_SMALL && (
        <AnalysisGlaphBigSmall
          analysisList={analysisList}
          summaryItems={summaryItems}
          analysisKind={analysisKind}
          comparisonKind={comparisonKind}
          categoryName={categoryName}
          keyTotal={keyTotal}
        />
      )}
    </Container>
  );
}
export default AnalysisCategory;
